import { parseMemoryCard, type CustomerStatus } from '@tenfour/contracts'
import { api } from '@/lib/api'
import { Customer } from '@/lib/types'

/** Structured address parts (#287/#564): the API composes the flat `address` from these. */
export type CustomerAddressInput = { street?: string; city?: string; state?: string; zip?: string }

function mapCustomer(raw: any): Customer {
  return {
    id: raw.id,
    customerNumber: raw.customerNumber ?? null,
    name: raw.name,
    phone: raw.phone,
    address: raw.address ?? undefined,
    // Structured address (#287) — the tax location's source of truth.
    street: raw.street ?? undefined,
    city: raw.city ?? undefined,
    state: raw.state ?? undefined,
    zip: raw.zip ?? undefined,
    email: raw.email ?? undefined,
    notes: raw.notes ?? undefined,
    contactStatus: raw.contactStatus ?? 'PROSPECT',
    // Record lifecycle — missing on an older payload means ACTIVE.
    status: raw.status ?? 'ACTIVE',
    isVip: raw.totalJobs >= 3,
    isTaxExempt: false,
    doNotText: raw.doNotText ?? false,
    consentStatus: raw.consentStatus ?? undefined,
    birthdayMonth: raw.birthdayMonth ?? null,
    birthdayDay: raw.birthdayDay ?? null,
    createdAt: raw.createdAt,
    lastJobAt: raw.lastJobAt ?? undefined,
    lastSeenAt: raw.lastSeenAt ?? undefined,
    totalJobs: raw.totalJobs ?? 0,
    totalSpent: Number(raw.totalBilled ?? 0),
    // Only the single-customer read carries the card; parseMemoryCard returns
    // null for missing/invalid/empty blobs, so the panel's empty state is the
    // one path for "nothing learned yet".
    memoryCard: parseMemoryCard(raw.memoryCard),
    memoryCardUpdatedAt: raw.memoryCardUpdatedAt ?? null,
  }
}

/**
 * The customer book. Server default is ACTIVE only, so every picker
 * gets the active list without asking; the customers workspace passes
 * `status: 'all'` and filters inactive rows into their own pill client-side.
 */
export async function listCustomers(
  accountId: string,
  status?: CustomerStatus | 'all',
): Promise<Customer[]> {
  const params = new URLSearchParams({ accountId })
  if (status) params.set('status', status)
  const raw = await api<any[]>(`/customers?${params.toString()}`)
  return raw.map(mapCustomer)
}

/** Single customer profile — backs the Conversations contact card. */
export async function getCustomer(customerId: string): Promise<Customer> {
  const raw = await api<any>(`/customers/${customerId}`)
  return mapCustomer(raw)
}

export async function addCustomer(
  accountId: string,
  // No flat `address` here on purpose: the API rejects a flat-only address
  // write (400) — send the structured parts and it composes the flat.
  data: CustomerAddressInput & { name: string; phone: string; email?: string; notes?: string; contactStatus?: string },
): Promise<{ customerId: string }> {
  const raw = await api<any>('/customers', {
    method: 'POST',
    body: JSON.stringify({ accountId, ...data }),
  })
  return { customerId: raw.id }
}

export async function updateCustomer(
  customerId: string,
  data: CustomerAddressInput & {
    name?: string
    phone?: string
    // Structured address only (#287/#564) — the API recomposes the flat
    // `address` from the parts (structured is the tax-location source of
    // truth) and refuses a flat-only write.
    email?: string
    notes?: string
    contactStatus?: string
    doNotText?: boolean
    birthdayMonth?: number | null
    birthdayDay?: number | null
  },
): Promise<void> {
  await api(`/customers/${customerId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

/**
 * Mark inactive / reactivate (plans/customer-status-model.md). Replaced
 * DELETE /customers/:id (now 410): the record — with its conversation
 * history, consent, jobs and invoices — is kept and merely hidden from lists.
 */
export async function setCustomerStatus(customerId: string, status: CustomerStatus): Promise<Customer> {
  const raw = await api<any>(`/customers/${customerId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  })
  return mapCustomer(raw)
}

/**
 * CSV export. Carries the structured Street/City/State/Zip columns beside the
 * flat Address so the file round-trips through the import, which (#564)
 * refuses a file whose only address column is the flat one. A legacy row that
 * has a flat address but no structured parts still exports (Address filled,
 * parts blank) and is reported as a per-row error on re-import — never
 * silently stored flat-only.
 */
export function exportCustomersCsv(customers: Customer[]): string {
  const header = 'Name,Phone,Address,Street,City,State,Zip,Email,Contact Status,Total Jobs,Total Billed,Last Job'
  const rows = customers.map((c) =>
    [
      c.name,
      c.phone,
      c.address ?? '',
      c.street ?? '',
      c.city ?? '',
      c.state ?? '',
      c.zip ?? '',
      c.email ?? '',
      c.contactStatus,
      c.totalJobs,
      c.totalSpent,
      c.lastJobAt ?? '',
    ]
      .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
      .join(','),
  )
  return [header, ...rows].join('\n')
}

export interface ImportCustomersResult {
  added: number
  skipped: Array<{ row: number; reason: string }>
  errors: Array<{ row: number; reason: string }>
}

/** Bulk-import customers from a CSV (name,phone required; email,notes,street,city,state,zip optional — a flat-only address column is refused). */
export async function importCustomersCsv(accountId: string, csv: string): Promise<ImportCustomersResult> {
  return api<ImportCustomersResult>(`/accounts/${accountId}/customers/import`, {
    method: 'POST',
    body: JSON.stringify({ csv }),
  })
}
